import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { GeoDataSource, type UserGeoData, type UserLocationData } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { debounce } from 'lodash';
import { defineStore } from 'pinia';
import { nextTick, ref } from 'vue';
import { useRoute, useRouter, type RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { useAuthStore } from './auth.store';

export const FALLBACK_LOCATION = {
  lat: 0,
  lng: 0,
};

const OBSERVER_INTERVAL = 1 * 60 * 1000; // 1 minute
const IP_LOCATION_MAX_AGE = 60 * 60 * 1000; // 1 hour
const BROWSER_LOCATION_MAX_AGE = 5 * 60 * 1000; // 5 minutes
// How long a precise reading outranks a coarser one. Matches the API.
const FIDELITY_MAX_AGE = 4 * 60 * 60 * 1000; // 4 hours

const SOURCES = [
  null,
  GeoDataSource.UNKNOWN,
  GeoDataSource.CLOUDFLARE_COUNTRY,
  GeoDataSource.CLOUDFLARE_IP,
  GeoDataSource.BROWSER_API,
];

// const mockFlare = {
//   response: {
//     city: 'Ljubljana',
//     country: 'SI',
//     latitude: 46.050367176003,
//     longitude: 14.499212180795935,
//   },
//   accuracy: 1,
//   source: GeoDataSource.CLOUDFLARE_IP,
//   location: { lat: 46.050367176003, lng: 14.499212180795935 },
//   updatedAt: new Date().toISOString(),
// };

export const isMapLocationError = (route: RouteLocationNormalizedLoadedGeneric) => {
  const { lat, lng } = route.query;
  const isLatLngQueryDefined = lat !== undefined && lng !== undefined;
  const castLat = parseFloat(String(lat));
  const castLng = parseFloat(String(lng));
  const isLatLngZero = castLat === 0 && castLng === 0;
  const isLatLngNaN = Number.isNaN(castLat) || Number.isNaN(castLng);

  return isLatLngQueryDefined && (isLatLngZero || isLatLngNaN);
};

export const useGeolocationStore = defineStore(GamStoreId.GEOLOCATION, () => {
  const router = useRouter();
  const route = useRoute();

  const cloudflareLocation = ref<UserLocationData | null>(null);
  const userLocation = ref<UserGeoData | null>(gambits.geolocationService.getLastLocation());
  const isUserLocated = ref<boolean>(!!gambits.geolocationService.getLastLocation());

  const needsPreciseLocation = ref<boolean>(false);
  const isLocationPermissionGranted = ref<boolean>(false);
  const isLocationPermissionDenied = ref<boolean>(false);
  const isLocationPermissionRequested = ref<boolean>(false);
  const isPermissionResolved = ref<boolean>(false);
  const shouldPanToChangedUserLocation = ref<boolean>(false);

  function onUserLocationChanged(geoData: UserGeoData) {
    userLocation.value = geoData;
    isUserLocated.value = true;

    const prevLocation = gambits.geolocationService.getLastLocation();
    const storedLocationAge = new Date().getTime() - new Date(prevLocation?.updatedAt || 0).getTime();
    const prevRank = SOURCES.indexOf(prevLocation?.source || null);
    const newRank = SOURCES.indexOf(geoData.source);

    if (prevLocation?.location.lat === geoData.location.lat && prevLocation?.location.lng === geoData.location.lng) {
      return;
    }

    // A coarser reading beats a stale precise one, so someone who travels
    // without their GPS on still stops showing up in the city they left.
    if (newRank < prevRank && storedLocationAge < FIDELITY_MAX_AGE) return;

    const maxAge = geoData.source === GeoDataSource.BROWSER_API ? BROWSER_LOCATION_MAX_AGE : IP_LOCATION_MAX_AGE;
    if (newRank === prevRank && storedLocationAge < maxAge) return;

    gambits.geolocationService.saveGeoLocation(geoData);

    const authStore = useAuthStore();
    if (!authStore.isAuthenticated() || !authStore.isActive()) return;

    gambits.userService.updateLocation(geoData);
  }

  const debouncedUserLocationChanged = debounce(onUserLocationChanged, 200, { leading: false });

  async function onObserveLocation() {
    const age = new Date().getTime() - new Date(userLocation.value?.updatedAt || 0).getTime();

    if (
      (userLocation.value?.source !== GeoDataSource.BROWSER_API && age < IP_LOCATION_MAX_AGE) ||
      (userLocation.value?.source === GeoDataSource.BROWSER_API && age < BROWSER_LOCATION_MAX_AGE)
    ) {
      return;
    }

    if (!isUserLocated.value) {
      const location = await determineCloudflareLocation();
      location && debouncedUserLocationChanged(location);
    }
  }

  const setNeedsPreciseLocation = () => {
    needsPreciseLocation.value = true;
  };

  const setLocationPermissionGranted = (value: boolean) => {
    isLocationPermissionGranted.value = value;
    if (value) isLocationPermissionDenied.value = false;
  };

  const onLocationPermissionDenied = () => {
    isLocationPermissionDenied.value = true;
    isLocationPermissionGranted.value = false;
  };

  const setLocationPermissionRequested = (value: boolean) => {
    isLocationPermissionRequested.value = value;
  };

  const setShouldPanToChangedUserLocation = (value: boolean) => {
    shouldPanToChangedUserLocation.value = value;
  };

  function fixBrokenMapUrl() {
    if (route.name === RouterNameType.LOCATIONS) {
      router.replace({
        ...route,
        query: { ...route.query, lat: undefined, lng: undefined, zoom: undefined },
      });
    }
  }

  function applyPermissionState(state: PermissionState) {
    if (state === 'granted') {
      isLocationPermissionGranted.value = true;
      isLocationPermissionDenied.value = false;
    } else if (state === 'denied') {
      isLocationPermissionGranted.value = false;
      isLocationPermissionDenied.value = true;
    } else {
      isLocationPermissionGranted.value = false;
      isLocationPermissionDenied.value = false;
    }
  }

  async function determinePermissionStatus() {
    if (!navigator.permissions?.query) {
      probeFromStoredLocation();
      return;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });

      applyPermissionState(permission.state);
      if (permission.state === 'granted') {
        handlePermissionGranted();
      }

      permission.onchange = () => {
        applyPermissionState(permission.state);
        if (permission.state === 'granted') {
          handlePermissionGranted();
        }
      };
    } catch {
      probeFromStoredLocation();
    }
  }

  function probeFromStoredLocation() {
    const lastLocation = gambits.geolocationService.getLastLocation();
    if (lastLocation?.source === GeoDataSource.BROWSER_API) {
      isLocationPermissionGranted.value = true;
    }
  }

  function handlePermissionGranted() {
    isLocationPermissionGranted.value = true;
    shouldPanToChangedUserLocation.value = true;
    if (!gambits.geolocationService.isTracking()) {
      gambits.geolocationService.getCurrentPosition();
      gambits.geolocationService.startTracking();
    }
  }

  async function determineCloudflareLocation(): Promise<UserGeoData | null> {
    let geoData = await gambits.geolocationService.getLocationFromIp();

    if (geoData) {
      cloudflareLocation.value = geoData?.response || null;
    } else {
      geoData = await gambits.geolocationService.getLocationFromCountry();
    }

    return geoData || null;
  }

  const initialize = async function () {
    await determineCloudflareLocation();

    await determinePermissionStatus();
    isPermissionResolved.value = true;
    await onObserveLocation();

    setInterval(onObserveLocation, OBSERVER_INTERVAL);

    if (isMapLocationError(route)) {
      nextTick(() => fixBrokenMapUrl());
    }

    router.afterEach((to) => {
      if (isMapLocationError(to)) {
        fixBrokenMapUrl();
      }
    });
  };

  function handlePositionSuccess(pos: GeolocationPosition, resolve: (coords: GeolocationCoordinates) => void) {
    setLocationPermissionGranted(true);
    const geoData = {
      location: { lat: pos.coords.latitude, lng: pos.coords.longitude },
      accuracy: pos.coords.accuracy,
      source: GeoDataSource.BROWSER_API,
      updatedAt: new Date().toISOString(),
    };
    userLocation.value = geoData;
    isUserLocated.value = true;
    debouncedUserLocationChanged(geoData);
    resolve(pos.coords);
  }

  function requestFreshPosition(): Promise<GeolocationCoordinates | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => handlePositionSuccess(pos, resolve),
        (error) => {
          if (error.code === GeolocationPositionError.PERMISSION_DENIED) {
            // Retry without enableHighAccuracy — handles cases where precise location is blocked
            navigator.geolocation.getCurrentPosition(
              (pos) => handlePositionSuccess(pos, resolve),
              (retryError) => {
                if (retryError.code === GeolocationPositionError.PERMISSION_DENIED) {
                  onLocationPermissionDenied();
                }
                resolve(null);
              },
              { enableHighAccuracy: false, maximumAge: 0, timeout: 10000 },
            );
            return;
          }
          resolve(null);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 },
      );
    });
  }

  return {
    userLocation,
    cloudflareLocation,
    onUserLocationChanged: debouncedUserLocationChanged,
    needsPreciseLocation,
    setNeedsPreciseLocation,
    isLocationPermissionGranted,
    isPermissionResolved,
    setLocationPermissionGranted,
    isLocationPermissionDenied,
    onLocationPermissionDenied,
    isLocationPermissionRequested,
    setLocationPermissionRequested,
    shouldPanToChangedUserLocation,
    setShouldPanToChangedUserLocation,
    isUserLocated,
    initialize,
    requestFreshPosition,
    requestLocationPermission: handlePermissionGranted,
  };
});
